/* cc-menu-drawer — mobile navigation drawer.
   Custom element; reparents itself to <body> (fixed descendants of
   the frosted header are containing-block trapped and composite
   ghosted — cc-burger-panel lesson). Open/close with scroll lock,
   Escape + overlay close, two-level accordion, and a compact
   searchable country picker that submits Dawn's localization form. */

class CCMenuDrawer extends HTMLElement {
  connectedCallback() {
    if (this.parentElement !== document.body) {
      document.body.appendChild(this); // re-triggers connectedCallback
      return;
    }
    if (this.__wired) return;
    this.__wired = true;

    this.panel = this.querySelector('.cc-menu-drawer__panel');
    this.opener = null;

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-cc-drawer-close]')) return this.close();

      const toggle = e.target.closest('.cc-menu-drawer__toggle');
      if (toggle) {
        // Composed view (750px+): the accordion is permanently open, so
        // a toggle has nothing to toggle -- the row acts as the LINK it
        // labels instead (Tony 2026-07-13: "shop button does nothing")
        const url = toggle.dataset.ccUrl;
        if (url && url !== '#' && window.matchMedia('(min-width: 750px)').matches) {
          window.location.href = url;
          return;
        }
        toggle.setAttribute('aria-expanded', toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      const countryToggle = e.target.closest('.cc-country__toggle');
      if (countryToggle) return this.toggleCountry(countryToggle);

      const option = e.target.closest('.cc-country__option');
      if (option) {
        const form = this.querySelector('.cc-country__form');
        form.querySelector('[data-cc-country-input]').value = option.dataset.value;
        form.submit();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    });

    const search = this.querySelector('.cc-country__search');
    if (search) {
      search.addEventListener('input', () => {
        const q = search.value.trim().toLowerCase();
        this.querySelectorAll('.cc-country__option').forEach((opt) => {
          opt.parentElement.hidden = q && !opt.textContent.toLowerCase().includes(q);
        });
      });
    }
  }

  toggleCountry(toggle) {
    const pop = this.querySelector('.cc-country__pop');
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    pop.hidden = expanded;
    if (!expanded) this.querySelector('.cc-country__search')?.focus();
  }

  open(opener) {
    this.opener = opener || null;
    // desktop: the Shop categories are ALWAYS laid out on the right —
    // pre-expand the accordion so CSS can place them (Tony 2026-07-12)
    if (window.matchMedia('(min-width: 750px)').matches) {
      this.querySelectorAll('.cc-menu-drawer__toggle').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    }
    // Phones (round 13): the sheet lives UNDER the header — anchor its
    // top to the real header stack bottom and morph the burger into the
    // X (same body.cc-burger-open morph as desktop, cc-header.css)
    if (true) { // all widths — one menu everywhere (Tony 2026-07-12)
      const wrap = document.querySelector('.header-wrapper');
      const dock = document.querySelector('.cc-header-dock');
      let top = wrap ? wrap.getBoundingClientRect().bottom : 0;
      if (dock) {
        const r = dock.getBoundingClientRect();
        if (r.height > 0) top = Math.max(top, r.bottom);
      }
      this.style.setProperty('--cc-drawer-top', Math.max(0, Math.round(top)) + 'px');
      document.body.classList.remove('cc-hdr-hidden');
      document.body.classList.add('cc-burger-open');
    }
    this.hidden = false;
    requestAnimationFrame(() => {
      this.classList.add('cc-menu-drawer--open');
      document.body.classList.add('cc-drawer-lock');
      this.querySelector('.cc-menu-drawer__close')?.focus();
    });
  }

  close() {
    if (this.hidden) return;
    this.classList.remove('cc-menu-drawer--open');
    document.body.classList.remove('cc-drawer-lock');
    document.body.classList.remove('cc-burger-open'); // un-morph the burger (phones)
    setTimeout(() => {
      this.hidden = true;
      // collapse the country pop for next open
      const pop = this.querySelector('.cc-country__pop');
      if (pop && !pop.hidden) this.toggleCountry(this.querySelector('.cc-country__toggle'));
      this.opener?.focus();
    }, 250); // after exit transition
  }
}

customElements.define('cc-menu-drawer', CCMenuDrawer);
